import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
} from "@floating-ui/react-dom";
import { ReactRenderer } from "@tiptap/react";
import { SuggestionKeyDownProps } from "@tiptap/suggestion";
import { GetEntitySuggestionsQuery } from "generated/graphql";
import { request } from "graphql-request";
import { queryClient } from "src/app";
import config from "src/config";
import { MentionOptions } from "./entity-mention";
import { getEntitySuggestionsDocument } from "./graphql";
import { MentionList } from "./mention-list";

const updatePosition = async ({
  clientRect,
  popupContainer,
}: {
  clientRect: (() => DOMRect | null) | null | undefined;
  popupContainer: HTMLDivElement | null;
}) => {
  if (!clientRect || !popupContainer) return;

  const { x, y } = await computePosition(
    {
      getBoundingClientRect: () => clientRect() ?? new DOMRect(),
    },
    popupContainer,
    {
      placement: "bottom-start",
      middleware: [
        offset(4),
        shift({ padding: 8 }),
        flip(),
        size({
          apply({ availableHeight }) {
            if (!popupContainer) return;
            popupContainer.style.maxHeight = `${availableHeight - 8}px`;
          },
        }),
      ],
    },
  );

  // Apply computed position
  popupContainer.style.left = `${x}px`;
  popupContainer.style.top = `${y}px`;
};

export const suggestion = ({
  accessToken,
  siteSlug,
  subdomain,
}: {
  accessToken: string | undefined;
  siteSlug: string | undefined;
  subdomain: string;
}): MentionOptions["suggestion"] => ({
  allowSpaces: true,
  items: async ({ query }) => {
    const queryKey = [
      (getEntitySuggestionsDocument.definitions[0] as any)?.name?.value,
      {
        search: query,
        collectionSiteSlug: siteSlug ?? null,
        organisationSubdomain: subdomain,
      },
    ];
    const cached =
      queryClient.getQueryData<GetEntitySuggestionsQuery>(queryKey);
    if (cached) {
      return cached?.org as Array<any>;
    }

    if (accessToken == null) {
      return [];
    }
    const res = await queryClient.fetchQuery({
      queryKey,
      queryFn: ({ queryKey }) =>
        request(
          config.publicApiUrl,
          getEntitySuggestionsDocument,
          queryKey[1] == null ? undefined : queryKey[1],
          accessToken == null
            ? undefined
            : { authorization: `Bearer ${accessToken}` },
        ),
    });

    return res?.org as Array<any>;
  },

  render: () => {
    let component: ReactRenderer<
      HTMLDivElement & { onKeyDown: (props: SuggestionKeyDownProps) => boolean }
    > | null;
    let popupContainer: HTMLDivElement | null;
    let cleanup: (() => void) | null = null;
    const keydownListener = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (popupContainer) {
          popupContainer.style.display = "none";
        }
      }
    };
    const outsideClickListener = (event: MouseEvent) => {
      if (popupContainer && !popupContainer.contains(event.target as Node)) {
        if (popupContainer) {
          popupContainer.style.display = "none";
        }
      }
    };

    return {
      onStart: async (props) => {
        if (!props.clientRect) return;

        popupContainer = document.createElement("div");
        popupContainer.className =
          "absolute overflow-y-auto no-scrollbar z-1000 rounded-lg border border-grey-200 bg-white p-1 shadow-lg pointer-events-auto min-w-80 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2";
        document.body.appendChild(popupContainer);

        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        popupContainer.appendChild(component.element);

        cleanup = autoUpdate(props.editor.view.dom, popupContainer, () =>
          updatePosition({ clientRect: props.clientRect, popupContainer }),
        );
        await updatePosition({ clientRect: props.clientRect, popupContainer });

        document.addEventListener("keydown", keydownListener);
        document.addEventListener("mousedown", outsideClickListener);
      },

      onExit: () => {
        if (component) {
          component.destroy();
          component = null;
        }

        if (popupContainer) {
          document.body.removeChild(popupContainer);
          popupContainer = null;
          document.removeEventListener("keydown", keydownListener);
          document.removeEventListener("mousedown", outsideClickListener);
        }

        if (cleanup) {
          cleanup();
          cleanup = null;
        }
      },

      onUpdate(props) {
        component?.updateProps(props);
        if (popupContainer) {
          popupContainer.style.display = "block";
        }

        if (!props.clientRect) {
          return;
        }

        updatePosition({ clientRect: props.clientRect, popupContainer });
      },

      onKeyDown(props) {
        if (component?.ref == null) {
          return false;
        }

        return component?.ref.onKeyDown(props);
      },
    };
  },
});
